import {
  compareDateOnly,
  dateOnly,
  type AllocationDraft,
  type DateOnly,
  type WholeMad,
} from "../../academics-obligations/src/index.js";

export const paymentsInstrumentsModule = "payments-instruments" as const;
export const PAYMENT_COUNTER_MAX = 999_999n;

export type PaymentMethod = "cash" | "cheque" | "transfer";

export interface CashIntent {
  readonly method: "cash";
}

export interface ChequeIntent {
  readonly method: "cheque";
  readonly chequeNumber: string;
  readonly bank: string;
  readonly drawer: string;
}

export interface TransferIntent {
  readonly method: "transfer";
  readonly senderLabel?: string;
  readonly bank: string;
  readonly reference: string;
  readonly statedDate: DateOnly;
}

export type PaymentMethodIntent = CashIntent | ChequeIntent | TransferIntent;

export interface PaymentDraft {
  readonly studentId: string;
  readonly amountMad: WholeMad;
  readonly receivedDate: DateOnly;
  readonly backdatingReason?: string;
  readonly note?: string;
  readonly method: PaymentMethodIntent;
  readonly allocations: readonly AllocationDraft[];
}

export interface FinalizedPayment extends PaymentDraft {
  readonly id: string;
  readonly reference: string;
  readonly deviceCode: string;
  readonly counter: string;
  readonly clientCreatedAt: string;
  readonly settlementStatus: "settled" | "pending";
  readonly finalized: true;
}

export interface ClockContext {
  readonly clientCreatedAt: string;
  readonly trustedObservedAt: string;
  readonly skewMilliseconds: number;
  readonly conspicuous: boolean;
}

export class PaymentValidationError extends Error {
  constructor(
    readonly code:
      | "INVALID_PAYMENT_METHOD"
      | "INVALID_INSTRUMENT"
      | "INVALID_RECEIVED_DATE"
      | "BACKDATING_REASON_REQUIRED"
      | "INVALID_REFERENCE"
      | "COUNTER_EXHAUSTED"
      | "INVALID_UUID"
      | "INVALID_CANONICAL_VALUE",
  ) {
    super(code);
  }
}

function cleanOptional(value: string | undefined): string | undefined {
  const cleaned = value?.trim();
  return cleaned ? cleaned : undefined;
}

function required(value: string): string {
  const cleaned = value.trim();
  if (!cleaned || cleaned.length > 120) throw new PaymentValidationError("INVALID_INSTRUMENT");
  return cleaned;
}

export function validateMethodIntent(value: PaymentMethodIntent): PaymentMethodIntent {
  if (value.method === "cash") {
    if (Object.keys(value).length !== 1) throw new PaymentValidationError("INVALID_PAYMENT_METHOD");
    return { method: "cash" };
  }
  if (value.method === "cheque")
    return {
      method: "cheque",
      chequeNumber: required(value.chequeNumber),
      bank: required(value.bank),
      drawer: required(value.drawer),
    };
  if (value.method === "transfer") {
    const senderLabel = cleanOptional(value.senderLabel);
    return {
      method: "transfer",
      ...(senderLabel ? { senderLabel } : {}),
      bank: required(value.bank),
      reference: required(value.reference),
      statedDate: dateOnly(value.statedDate),
    };
  }
  throw new PaymentValidationError("INVALID_PAYMENT_METHOD");
}

export function validateReceivedDate(input: {
  readonly receivedDate: DateOnly;
  readonly today: DateOnly;
  readonly activeFrom: DateOnly;
  readonly activeThrough: DateOnly;
  readonly backdatingReason?: string;
}): void {
  if (compareDateOnly(input.receivedDate, input.today) > 0)
    throw new PaymentValidationError("INVALID_RECEIVED_DATE");
  if (
    compareDateOnly(input.receivedDate, input.activeFrom) < 0 ||
    compareDateOnly(input.receivedDate, input.activeThrough) > 0
  )
    throw new PaymentValidationError("INVALID_RECEIVED_DATE");
  if (
    compareDateOnly(input.receivedDate, input.today) < 0 &&
    !cleanOptional(input.backdatingReason)
  )
    throw new PaymentValidationError("BACKDATING_REASON_REQUIRED");
}

export function clockContext(
  clientCreatedAt: string,
  trustedObservedAt: string,
  thresholdMilliseconds = 5 * 60 * 1_000,
): ClockContext {
  const client = Date.parse(clientCreatedAt);
  const trusted = Date.parse(trustedObservedAt);
  if (!Number.isFinite(client) || !Number.isFinite(trusted))
    throw new PaymentValidationError("INVALID_RECEIVED_DATE");
  const skewMilliseconds = client - trusted;
  return {
    clientCreatedAt,
    trustedObservedAt,
    skewMilliseconds,
    conspicuous: Math.abs(skewMilliseconds) >= thresholdMilliseconds,
  };
}

export function paymentReference(deviceCode: string, counter: bigint): string {
  if (!/^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{5}$/.test(deviceCode))
    throw new PaymentValidationError("INVALID_REFERENCE");
  if (counter < 1n || counter > PAYMENT_COUNTER_MAX)
    throw new PaymentValidationError("COUNTER_EXHAUSTED");
  return `PAY-${deviceCode}-${counter.toString().padStart(6, "0")}`;
}

export function isUuidV4(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function uuidV4(): string {
  const value = crypto.randomUUID();
  if (!isUuidV4(value)) throw new PaymentValidationError("INVALID_UUID");
  return value;
}

export type CanonicalValue =
  null | boolean | string | readonly CanonicalValue[] | { readonly [key: string]: CanonicalValue };

export function canonicalJson(value: CanonicalValue): string {
  if (value === null || typeof value === "boolean" || typeof value === "string")
    return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => canonicalJson(item)).join(",")}]`;
  if (typeof value === "object") {
    const record = value as Readonly<Record<string, CanonicalValue>>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key]!)}`)
      .join(",")}}`;
  }
  throw new PaymentValidationError("INVALID_CANONICAL_VALUE");
}

export interface EnvelopeBinding {
  readonly actorUserId: string;
  readonly installationId: string;
  readonly deploymentId: string;
  readonly issuanceSnapshot: CanonicalValue;
}

export function canonicalEnvelope(
  payment: FinalizedPayment,
  issuanceSnapshotId: string,
  binding?: EnvelopeBinding,
) {
  const method: CanonicalValue =
    payment.method.method === "cash"
      ? { method: "cash" }
      : payment.method.method === "cheque"
        ? {
            bank: payment.method.bank,
            chequeNumber: payment.method.chequeNumber,
            drawer: payment.method.drawer,
            method: "cheque",
          }
        : {
            bank: payment.method.bank,
            method: "transfer",
            reference: payment.method.reference,
            ...(payment.method.senderLabel ? { senderLabel: payment.method.senderLabel } : {}),
            statedDate: payment.method.statedDate,
          };
  const value: CanonicalValue = {
    allocations: payment.allocations.map((allocation) => ({
      amountMad: allocation.amountMad.toString(),
      obligationId: allocation.obligationId,
    })),
    amountMad: payment.amountMad.toString(),
    ...(payment.backdatingReason ? { backdatingReason: payment.backdatingReason.trim() } : {}),
    clientCreatedAt: payment.clientCreatedAt,
    counter: payment.counter,
    deviceCode: payment.deviceCode,
    envelopeVersion: binding ? "phase-03-v1" : "phase-02-v1",
    id: payment.id,
    issuanceSnapshotId,
    method,
    ...(payment.note?.trim() ? { note: payment.note.trim() } : {}),
    receivedDate: payment.receivedDate,
    reference: payment.reference,
    settlementStatus: payment.settlementStatus,
    studentId: payment.studentId,
    ...(binding
      ? {
          actorUserId: binding.actorUserId,
          deploymentId: binding.deploymentId,
          installationId: binding.installationId,
          issuanceSnapshot: binding.issuanceSnapshot,
        }
      : {}),
  };
  return new TextEncoder().encode(canonicalJson(value));
}
